// Print plumbing for the v3 deck. Same mechanism as v2 (ValueStoryDeck): the deck is a fixed
// 1920x1080 <deck-stage>, so "Download PDF" is window.print() and the ROI one-pager is the same
// print with a body class that styles.css uses to hide every slide but `[data-vc-roi-slide]`.
// Copied rather than imported because v2's deck is deleted once the route switches over.

/** Body class marking an ROI-only print; the rules live in styles.css (`body.vc-print-roi`). */
export const PRINT_ROI_CLASS = 'vc-print-roi';

/**
 * Resolve the self-hosted Roobert faces (a print fired earlier renders in the fallback stack),
 * finish in-flight transitions inside this app, then yield a frame so layout is committed.
 */
export async function settleForPrint(): Promise<void> {
  try {
    await document.fonts.ready;
  } catch {
    /* no Font Loading API — printing with whatever loaded beats not printing */
  }
  if (typeof document.getAnimations === 'function') {
    const scope = document.querySelector('.vc-root');
    for (const a of document.getAnimations()) {
      const target = (a.effect as KeyframeEffect | null)?.target;
      if (scope && target && !scope.contains(target)) continue;
      try {
        a.finish();
      } catch {
        /* an infinite animation has no finish state */
      }
    }
  }
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
}

export async function printDeck(): Promise<void> {
  await settleForPrint();
  window.print();
}

/**
 * The ROI one-pager. The class is cleared on `afterprint` only: window.print() may return before
 * the page is captured, so clearing on a timer would print the whole deck. The leading clear and
 * the deck's unmount cleanup cover paths where `afterprint` never fires.
 */
export async function printRoiOnePager(): Promise<void> {
  const clear = () => document.body.classList.remove(PRINT_ROI_CLASS);
  clear();
  window.addEventListener('afterprint', clear, { once: true });
  document.body.classList.add(PRINT_ROI_CLASS);
  await settleForPrint();
  window.print();
}
